import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  FormControlLabel,
  Checkbox,
  createTheme,
} from "@material-ui/core";
import { useHistory, useParams } from "react-router-dom";
import {
  deleteCategoria,
  getCategoriaById,
  postCategoria,
  updateCategoria,
} from "../../../../api/categoria";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import MultilanguageTabBlock from "../../../components/MultilanguageTabBlock";
import { getNonEmpty } from "../../../../utils/helpers";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";

// Create theme for delete button (red)
const theme = createTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyCategoria() {
  return {
    titulo: "",
    active: true,
    showInActualidad: true,
  };
}

export default function EditCategoriaPage() {
  const [categoria, setCategoria] = useState(getEmptyCategoria());
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const categoriaId = useParams().id;
  const history = useHistory();

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    if (!categoriaId) {
      disableLoadingData();
      return;
    }
    getCategoriaById(categoriaId)
      .then((res) => {
        if (res.status === 200) {
          setCategoria(res.data);
          disableLoadingData();
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "Could not get categoria.",
        });
        history.push("/categorias");
      });
  }, [categoriaId, disableLoadingData, history]);

  function saveCategoria() {
    let saveCategoria = categoria;
    if (!categoriaId) {
      postCategoria(saveCategoria)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Categoria successfully created.",
            });
            history.push("/categorias");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save categoria.",
          });
        });
    } else {
      updateCategoria(categoriaId, saveCategoria)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Changes successfully saved.",
            });
            history.push("/categorias");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  const handleChange = (element, lang) => (event) => {
    if (lang) {
      if (event.target.value === " ") return;

      if (!categoria[element]) categoria[element] = {};
      let newText = categoria[element];
      newText[lang] = event.target.value;
      setCategoria({ ...categoria, [element]: newText });
    } else setCategoria({ ...categoria, [element]: event.target.value });
  };

  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    setObj,
    obj,
    showDelete = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Botón Back */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>
                {/* Botones Save y Delete */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>

                  {showDelete && (
                    <MuiThemeProvider theme={theme}>
                      <Button
                        onClick={onDelete}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className="text-nowrap"
                      >
                        Delete
                      </Button>
                    </MuiThemeProvider>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMultilanguageTabContent = (lang) => {
    return (
      <>
        <TextField
          id={`titulo`}
          label="Titulo"
          value={
            categoria.titulo && categoria.titulo[lang]
              ? categoria.titulo[lang]
              : ""
          }
          onChange={handleChange("titulo", lang)}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="outlined"
          required
        />
      </>
    );
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          {renderStickyButtons({
            onBack: () => history.push("/categorias"),
            onSave: saveCategoria,
            onDelete: () => setOpenConfirmDialog(true),
            showDelete: !!categoriaId,
            setObj: setCategoria,
            obj: categoria,
          })}
          <CardHeader title="Editar Categoría"></CardHeader>
          <CardBody>
            <MultilanguageTabBlock
              multilanguageTabContent={renderMultilanguageTabContent}
            />

            {/* Checkboxes */}
            <div style={{ marginTop: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={categoria.active || false}
                    onChange={() =>
                      setCategoria({ ...categoria, active: !categoria.active })
                    }
                    name="checkActive"
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={categoria.showInActualidad !== false}
                    onChange={() =>
                      setCategoria({
                        ...categoria,
                        showInActualidad: categoria.showInActualidad === false,
                      })
                    }
                    name="checkShowInActualidad"
                  />
                }
                label="Mostrar en Actualidad"
              />
            </div>
          </CardBody>
        </Card>
        {categoriaId && (
          <>
            <ConfirmDialog
              title={"Are you sure you want to delete this categoria?"}
              open={openConfirmDialog}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteCategoria(categoriaId)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: "Categoria deleted successfully",
                      });
                      history.push("/categorias");
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not delete categoria.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
